<script setup lang="ts">import {useRoute} from "vue-router";
import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";
import yapi from "@/lib/yapi";
import {Cohort} from "@YenzaCT/sdk";
import {onMounted, ref, computed, defineAsyncComponent} from "vue";
import {useApiCall} from "@/composables/useApiCall";
import PageHeader from "@/components/PageHeader.vue";

import {useReportingStore} from "@/store/reporting";
import {useRoutedTabbedDashboard} from "@/composables/useRoutedTabbedDashboard";
import {useGlobalStore} from "@/store";

// Lazy load tab components
const DashboardTab = defineAsyncComponent(() => import("./tabs/DashboardTab.vue"));
const StudentsTab = defineAsyncComponent(() => import("./tabs/StudentsTab.vue"));
const LicensesTab = defineAsyncComponent(() => import("./tabs/LicensesTab.vue"));
const ChoicesTab = defineAsyncComponent(() => import("../ui/ChoicesTab.vue"));
const AssessmentsTab = defineAsyncComponent(() => import("../ui/AssessmentsTab.vue"));

const route = useRoute();
const {executeApi} = useApiCall();
const store = useGlobalStore();
const id = route.params.cohortId as string;
const tenantId = route.params.tenantId as string;
const institutionId = route.params.institutionId as string;
const initialized = ref(false);
const reportingStore = useReportingStore();
const {activeTab} = useRoutedTabbedDashboard("cohortDashboard", id);
const canAccessSpecificTenant = store.canAccessSpecificTenant(tenantId);

const cohort = ref<Cohort>();

// Map tab names to components
const tabComponents = {
  dashboard: DashboardTab,
  students: StudentsTab,
  licenses: LicensesTab,
  choices: ChoicesTab,
  assessments: AssessmentsTab
};

// Get the current tab component
const currentTabComponent = computed(() =>
  tabComponents[activeTab.value as keyof typeof tabComponents] || DashboardTab);

onMounted(async () => {
  initialized.value = false;
  // Set filtering in the store to ensure all metrics and reports are scoped to this institution
  reportingStore.setFiltersAndRefresh({cohorts: [id]});

  await fetchCohort();
  initialized.value = true;
});

async function fetchCohort() {
  const result = await executeApi(async () => {
    const response = await yapi.admin.cohort.get(id);
    return response.data;
  });
  if (result) {
    cohort.value = result;
  }
}
</script>

<template>
  <authenticated-layout v-if="cohort" :back-route="{
                          name: 'institutionDashboard',
                          params: { institutionId: institutionId, tab: 'cohorts' }
                        }"
                        :breadcrumbs="[
                          { text: 'My Schools', to: { name: 'schoolsDashboard' } },
                          { text: cohort?.tenantMeta?.title || '',
                            to: canAccessSpecificTenant ? { name: 'schoolsDashboardWithTenant',
                                                            params: { tenantId: cohort?.tenantMeta._id } } : {
                                                              name: 'schoolsDashboardWithoutTenant'
                                                            }
                          },
                          { text: cohort.institutionMeta?.title || '',
                            to: { name: 'institutionDashboard', params: { institutionId: cohort.institutionMeta._id } } },
                          { text: cohort.title || '' }
                        ]">

    <page-header
      :title="cohort?.title"
    />

    <v-container class="mt-0 mx-auto pa-0 border-b" fluid>
      <v-tabs v-model="activeTab" grow>
        <v-tab value="dashboard">
          Dashboard
        </v-tab>
        <v-tab value="students">
          Students
        </v-tab>
        <v-tab value="licenses">
          Licenses
        </v-tab>
        <v-tab value="choices">
          Choices
        </v-tab>
        <v-tab value="assessments">
          Assessments
        </v-tab>
      </v-tabs>
    </v-container>

    <div v-if="cohort" class="mt-2">
      <v-container class="mx-auto" fluid>
        <component
          v-if="initialized"
          :is="currentTabComponent"
          :cohort="cohort"
        />
      </v-container>
    </div>

  </authenticated-layout>
</template>

<style scoped>

</style>
